import React from 'react';
import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  GraduationCap,
  PlayCircle,
  Sparkles,
} from 'lucide-react';
import { CourseDetails, Lesson, Unit } from '../../hooks/useCourseApi';
import { cardTheme } from '../../styles/cardTheme';

export interface EnrolledCourseItem {
  id: string;
  courseName: string;
  term: string;
  grade: string;
  description: string;
  imagePath: string;
  progress?: number;
}

interface EnrolledCourseCardProps {
  course: EnrolledCourseItem;
  imageSrc: string;
  curriculum?: CourseDetails;
  isExpanded: boolean;
  isLoadingCurriculum: boolean;
  expandedUnitIndexes: Set<number>;
  onToggleCurriculum: () => void;
  onToggleUnit: (unitIndex: number) => void;
  onContinue: () => void;
  getLessonTypeIcon: (lesson: Lesson) => React.ReactNode;
}

const getTermLabel = (term: string) =>
  term === 'First' ? 'الترم الأول' : term === 'Second' ? 'الترم الثاني' : term;

const getGradeLabel = (grade: string) => {
  const labels: Record<string, string> = {
    Secondary1: 'أولى ثانوي',
    Secondary2: 'ثانية ثانوي',
    Secondary3: 'ثالثة ثانوي',
  };
  return labels[grade] || grade;
};

const EnrolledCourseCard: React.FC<EnrolledCourseCardProps> = ({
  course,
  imageSrc,
  curriculum,
  isExpanded,
  isLoadingCurriculum,
  expandedUnitIndexes,
  onToggleCurriculum,
  onToggleUnit,
  onContinue,
  getLessonTypeIcon,
}) => {
  const progress = course.progress ?? 0;

  return (
    <article className={`${cardTheme.shell} ${cardTheme.accentTop} overflow-hidden transition-all duration-300 hover:shadow-lg`}>
      {/* Header with thumbnail */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 sm:p-5 bg-gradient-to-l from-green-50/60 via-white to-gold-50/30 border-b border-green-100">
        <div className="relative w-full sm:w-40 h-32 sm:h-36 shrink-0 rounded-xl overflow-hidden border-2 border-gold-300/60 shadow-md group">
          <img
            src={imageSrc}
            alt={course.courseName}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = '/default-course.jpg';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 to-transparent" />
          <div className="absolute top-2 right-2 w-7 h-7 bg-gold-500 rounded-full flex items-center justify-center shadow border-2 border-white">
            <CheckCircle2 className="w-4 h-4 text-green-950" />
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
          <div>
            <h3 className={`text-lg sm:text-xl font-bold mb-2 line-clamp-2 ${cardTheme.title}`}>
              {course.courseName}
            </h3>
            <div className="flex flex-wrap gap-2">
              {course.term && (
                <span className={cardTheme.badgeGold}>{getTermLabel(course.term)}</span>
              )}
              {course.grade && (
                <span className={cardTheme.badge}>
                  <GraduationCap className="w-3.5 h-3.5" />
                  {getGradeLabel(course.grade)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onContinue}
            className={`inline-flex items-center justify-center gap-2 self-start ${cardTheme.btnPrimary} text-sm py-2.5 px-5 rounded-xl shadow-sm`}
          >
            <PlayCircle className="w-4 h-4" />
            كمّل التعلم
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="px-4 sm:px-5 py-4 border-b border-green-100 bg-white">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-green-800 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-gold-500" />
            تقدمك في الكورس
          </p>
          <span className="text-sm font-bold text-gold-700 bg-gold-50 border border-gold-200 px-3 py-0.5 rounded-full">
            {progress}%
          </span>
        </div>
        <div className="h-3 rounded-full bg-green-100 overflow-hidden shadow-inner">
          <div
            className="h-full rounded-full bg-gradient-to-l from-green-800 via-green-700 to-gold-500 transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Curriculum accordion */}
      <div className="p-4 sm:p-5">
        <button
          type="button"
          onClick={onToggleCurriculum}
          className="w-full flex items-center justify-between gap-3 p-3 sm:p-4 rounded-xl bg-green-50 border border-green-100 hover:bg-green-100/80 hover:border-gold-300/50 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cardTheme.iconBox}`}>
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-base sm:text-lg font-bold text-green-900">رحلة الدرس</span>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-green-700 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {isExpanded && (
          <div className="mt-4 rounded-xl border border-green-100 bg-gradient-to-b from-white to-green-50/30 p-3 sm:p-4">
            {isLoadingCurriculum ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-200 border-t-green-700" />
              </div>
            ) : curriculum?.units && curriculum.units.length > 0 ? (
              <div className="space-y-3">
                {curriculum.units.map((unit: Unit, unitIndex: number) => {
                  const isUnitExpanded = expandedUnitIndexes.has(unitIndex);
                  const unitLessons = unit.lessons || [];

                  return (
                    <div
                      key={unit.id}
                      className="rounded-xl border border-green-100 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <button
                        type="button"
                        onClick={() => onToggleUnit(unitIndex)}
                        className="w-full flex items-center gap-3 p-3 sm:p-4 text-right hover:bg-green-50/50 transition-colors"
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                            isUnitExpanded ? 'bg-green-700' : 'bg-green-100'
                          }`}
                        >
                          <ChevronDown
                            className={`w-4 h-4 transition-transform duration-200 ${
                              isUnitExpanded ? 'rotate-180 text-white' : 'text-green-700 -rotate-90'
                            }`}
                          />
                        </div>
                        <span className="flex-1 text-sm sm:text-base font-bold text-green-900 text-right">
                          {unit.unitName}
                        </span>
                        <span className="text-xs font-semibold text-gold-700 bg-gold-50 border border-gold-200 px-2.5 py-1 rounded-full shrink-0">
                          {unitLessons.length} درس
                        </span>
                      </button>

                      {isUnitExpanded && (
                        <div className="relative px-3 pb-3 sm:px-4 sm:pb-4 border-t border-green-50">
                          {unitLessons.length > 1 && (
                            <div className="absolute right-5 top-4 bottom-4 w-0.5 bg-gradient-to-b from-gold-400 to-green-200 rounded-full" />
                          )}
                          <div className="space-y-2.5 pt-3">
                            {unitLessons.map((lesson: Lesson) => {
                              const isCompleted =
                                (lesson.type === 'Video' && (lesson as Lesson & { isCompleted?: boolean }).isCompleted) ||
                                (lesson.type === 'Quiz' && (lesson as Lesson & { isQuizSubmitted?: boolean }).isQuizSubmitted) ||
                                (lesson.type === 'Assignment' && (lesson as Lesson & { isAssignmentSubmitted?: boolean }).isAssignmentSubmitted) ||
                                (lesson.type === 'File' && (lesson as Lesson & { isFileOpened?: boolean }).isFileOpened);

                              return (
                                <div key={lesson.id} className="relative flex items-start gap-2 pr-1">
                                  <div
                                    className={`relative z-10 w-5 h-5 rounded-full shrink-0 mt-3 border-2 border-white shadow ${
                                      isCompleted ? 'bg-gold-500' : 'bg-green-200'
                                    }`}
                                  />
                                  <button
                                    type="button"
                                    onClick={onContinue}
                                    className={`flex-1 text-right rounded-xl border p-3 sm:p-3.5 transition-all hover:shadow-md ${
                                      isCompleted
                                        ? 'bg-gold-50/80 border-gold-200 hover:border-gold-400'
                                        : 'bg-white border-green-100 hover:border-green-300'
                                    }`}
                                  >
                                    <div className="flex items-start gap-2.5">
                                      <div
                                        className={`p-2 rounded-lg shrink-0 ${
                                          isCompleted ? 'bg-green-700 text-white' : 'bg-green-50 text-green-700'
                                        }`}
                                      >
                                        {getLessonTypeIcon(lesson)}
                                      </div>
                                      <span
                                        className={`text-sm font-semibold leading-relaxed ${
                                          isCompleted ? 'text-green-900' : 'text-gray-700'
                                        }`}
                                      >
                                        {lesson.lessonName}
                                      </span>
                                      {isCompleted && (
                                        <CheckCircle2 className="w-5 h-5 text-gold-600 shrink-0 mr-auto" />
                                      )}
                                    </div>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-sm text-gray-500 py-6">لا يوجد محتوى متاح حالياً</p>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default EnrolledCourseCard;
